// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { constants } from "@werner/XDL/constants";
import { color, mergeWithCss, xScreen } from "@werner/XDL/Utilities";
import React, { useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    leftTitle?: string;
    rightTitles?: string[];
    extendedStyles?: LinesHeaderStyles;
    orderLineStyle?: boolean;
}

export interface LinesHeaderStyles {
    wrapper?: InjectableCss;
    itemDescription?: InjectableCss;
    rightSection?: InjectableCss;
    qty?: InjectableCss;
    priceUnit?: InjectableCss;
    total?: InjectableCss;
}

export const linesHeaderStyles: LinesHeaderStyles = {
    wrapper: {
        css: css`
            display: flex;
            padding: 16px 0 12px;
            font-size: 14px;
            letter-spacing: 0;
            line-height: 22px;
            font-weight: 500;
            width: 100%;
            border-bottom: 1px solid ${color("common.ui1")};
        `,
    },
    itemDescription: {
        css: css`
            width: 580px;
            margin-right: 12px;
            &.orderLineStyle {
                width: 810px;
            }
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                width: 100%;
            }
            @media print {
                width: 436px;
            }
        `,
    },
    rightSection: {
        css: css`
            flex-grow: 1;
            display: flex;

            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                display: none;
            }
            @media print {
                display: flex;
                width: calc(100% - 436px);
                justify-content: space-between;
            }
        `,
    },
    qty: {
        css: css`
            flex: 1;
            @media print {
                text-align: left;
                word-break: initial;
            }
        `,
    },
    priceUnit: {
        css: css`
            flex: 1;
            text-align: center;
            @media print {
                text-align: left;
                word-break: initial;
            }
        `,
    },
    total: {
        css: css`
            flex: 1;
            text-align: right;
            padding-right: 10px;
            @media print {
                flex: 0;
                text-align: left;
                word-break: initial;
            }
        `,
    },
};

const LinesHeader = ({ leftTitle, rightTitles, orderLineStyle, extendedStyles }: OwnProps) => {
    const [styles] = useState(() => mergeWithCss(linesHeaderStyles, extendedStyles));
    const orderLineStyleClassName = orderLineStyle ? "orderLineStyle" : "";

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.itemDescription} className={orderLineStyleClassName}>
                <Typography>{translate(leftTitle || "")}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.rightSection}>
                <StyledWrapper {...styles.qty}>{translate(rightTitles?.[0] || "")}</StyledWrapper>
                <StyledWrapper {...styles.priceUnit}>{translate(rightTitles?.[1] || "")}</StyledWrapper>
                {rightTitles?.[2] && (
                    <StyledWrapper {...styles.total}>{translate(rightTitles?.[2] || "")}</StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

LinesHeader.defaultProps = {
    leftTitle: translate("Item Description"),
    rightTitles: ["QTY", "Price/Unit", "Total"],
};

export default LinesHeader;
